import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, ArrowUp, ArrowDown, ArrowRight, Minus, ChevronUp, ChevronDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../utils/api';
import { formatDateBR, todayLocalISO } from '../utils/formatDate';
import { useConfig } from '../context/ConfigContext';
import toast from 'react-hot-toast';
import AnimalSearchSelect from '../components/AnimalSearchSelect';
import { formatGd, direcaoGmd, classificarGmd, limiaresGmd, CORES_GMD } from '../utils/gmd';

const CATEGORIAS = ['cordeiro','borrego','reprodutor','matriz'];

export default function Pesagens() {
  const { config } = useConfig();
  const [pesagens, setPesagens] = useState([]);
  const [animais, setAnimais] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [filtro, setFiltro] = useState({ animal_id:'', categoria:'', lote_id:'', data_inicio:'', data_fim:'', busca:'', ate_300_dias:false });
  const [sort, setSort] = useState({ col:'data', dir:'desc' });
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ animal_id:'', data:todayLocalISO(), peso:'', observacoes:'' });
  const [loading, setLoading] = useState(true);
  const [graficoAnimal, setGraficoAnimal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { animal_id:filtro.animal_id, categoria:filtro.categoria, lote_id:filtro.lote_id, data_inicio:filtro.data_inicio, data_fim:filtro.data_fim, busca:filtro.busca, ate_300_dias: filtro.ate_300_dias ? '1' : '' };
      const { data } = await api.get('/pesagens', { params });
      setPesagens(data);
    } finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/animais', { params:{ situacao:'ativo', limit:500 } }).then(r => setAnimais(r.data.data));
    api.get('/lotes').then(r => setLotes(r.data));
  }, []);

  const toggleSort = (col) => setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }));
  const SortIcon = ({ col }) => sort.col === col ? (sort.dir === 'asc' ? <ChevronUp size={13}/> : <ChevronDown size={13}/>) : <ChevronDown size={13} style={{ opacity:0.3 }}/>;

  const sorted = [...pesagens].sort((a, b) => {
    let va = a[sort.col] ?? ''; let vb = b[sort.col] ?? '';
    if (['peso','gmd','gmd_acumulado','idade_dias'].includes(sort.col)) { va = parseFloat(va)||0; vb = parseFloat(vb)||0; }
    if (typeof va === 'number') return sort.dir === 'asc' ? va - vb : vb - va;
    return sort.dir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
  });

  const salvar = async () => {
    try {
      const { data } = await api.post('/pesagens', form);
      toast.success(`Pesagem registrada! GMD: ${data.gmd ? formatGd(data.gmd) : 'N/A (primeira pesagem)'}`);
      setShowModal(false);
      setForm(p => ({ ...p, peso:'', observacoes:'' }));
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro ao salvar'); }
  };

  const abrirModal = () => {
    setForm(p => ({ ...p, animal_id:'' }));
    setShowModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
  };

  const remover = async (id) => {
    if (!window.confirm('Remover esta pesagem?')) return;
    try { await api.delete(`/pesagens/${id}`); toast.success('Removida'); load(); }
    catch { toast.error('Erro ao remover'); }
  };

  // Cor = desempenho vs. parâmetros configurados, em g/dia (independe do sinal).
  const gmdColor = (v) => {
    const nivel = classificarGmd(v, config);
    return nivel ? CORES_GMD[nivel].color : 'var(--text-secondary)';
  };

  // Direção = sinal do GMD. Positivo (mesmo baixo) SEMPRE ↑; zero → ; negativo ↓.
  const gmdArrow = (v) => {
    const dir = direcaoGmd(v);
    if (dir === 'up') return <ArrowUp size={14} />;
    if (dir === 'down') return <ArrowDown size={14} />;
    if (v == null || v === '' || isNaN(parseFloat(v))) return <Minus size={14} />;
    return <ArrowRight size={14} />;
  };

  const dadosGrafico = graficoAnimal
    ? pesagens.filter(p => p.animal_id === parseInt(graficoAnimal)).map(p => ({
        data: formatDateBR(p.data),
        peso: parseFloat(p.peso),
      })).reverse()
    : [];

  const COLS = [
    { col:'brinco', label:'Animal' }, { col:'categoria', label:'Categoria' },
    { col:'data', label:'Data' }, { col:'peso', label:'Peso' },
    { col:'idade_dias', label:'Idade' }, { col:'gmd', label:'GMD (g/d)' },
    { col:'gmd_acumulado', label:'GMD Acumulado (g/d)' }, { col:'observacoes', label:'Obs' },
  ];

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div><h1>Pesagens</h1><p>{pesagens.length} registros · histórico de peso e GMD</p></div>
        <button className="btn btn-primary" onClick={abrirModal}><Plus size={16} /> Nova Pesagem</button>
      </div>

      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
          <div className="search-box" style={{ flex:2, minWidth:200 }}>
            <Search size={16} />
            <input placeholder="Buscar por brinco ou nome..." value={filtro.busca} onChange={e => setFiltro(p => ({ ...p, busca:e.target.value }))} />
          </div>
          <div style={{ width:260, minWidth:220 }}>
            <AnimalSearchSelect value={filtro.animal_id} onChange={id => setFiltro(p => ({ ...p, animal_id:id }))} emptyLabel="Todos os animais" />
          </div>
          <select value={filtro.categoria} onChange={e => setFiltro(p => ({ ...p, categoria:e.target.value }))} style={{ width:'auto' }}>
            <option value="">Todas categorias</option>
            {CATEGORIAS.map(c => <option key={c} value={c} style={{ textTransform:'capitalize' }}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
          </select>
          <select value={filtro.lote_id} onChange={e => setFiltro(p => ({ ...p, lote_id:e.target.value }))} style={{ width:'auto' }}>
            <option value="">Todos os lotes</option>
            {lotes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
          </select>
          <div>
            <label style={{ fontSize:12, color:'var(--text-secondary)', display:'block', marginBottom:4 }}>Início</label>
            <input type="date" value={filtro.data_inicio} onChange={e => setFiltro(p => ({ ...p, data_inicio:e.target.value }))} style={{ width:145 }} />
          </div>
          <div>
            <label style={{ fontSize:12, color:'var(--text-secondary)', display:'block', marginBottom:4 }}>Fim</label>
            <input type="date" value={filtro.data_fim} onChange={e => setFiltro(p => ({ ...p, data_fim:e.target.value }))} style={{ width:145 }} />
          </div>
          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, whiteSpace:'nowrap', paddingBottom:8 }}>
            <input type="checkbox" checked={filtro.ate_300_dias} onChange={e => setFiltro(p => ({ ...p, ate_300_dias:e.target.checked }))} />
            Animais até 300 dias
          </label>
          {filtro.animal_id && (
            <button className="btn btn-secondary btn-sm" onClick={() => setGraficoAnimal(g => g ? null : filtro.animal_id)}>
              {graficoAnimal ? 'Ocultar curva' : 'Ver curva de peso'}
            </button>
          )}
        </div>
      </div>

      {graficoAnimal && dadosGrafico.length > 1 && (
        <div className="card" style={{ marginBottom:16 }}>
          <h3 style={{ marginBottom:16, fontSize:15 }}>Curva de Crescimento</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={dadosGrafico}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
              <XAxis dataKey="data" tick={{ fontSize:12 }} />
              <YAxis tick={{ fontSize:12 }} unit="kg" />
              <Tooltip formatter={(v) => [`${v} kg`, 'Peso']} />
              <Line type="monotone" dataKey="peso" stroke="var(--color-primary)" strokeWidth={2} dot={{ r:4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <table>
          <thead><tr>
            {COLS.map(({ col, label }) => (
              <th key={col} style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }} onClick={() => toggleSort(col)}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>{label} <SortIcon col={col} /></span>
              </th>
            ))}
            <th></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} style={{ textAlign:'center', padding:40, color:'var(--text-secondary)' }}>Carregando...</td></tr>
            : sorted.length === 0 ? <tr><td colSpan={9}><div className="empty-state"><p>Nenhuma pesagem encontrada</p></div></td></tr>
            : sorted.map(p => (
              <tr key={p.id}>
                <td><strong style={{ color:'var(--color-primary)' }}>{p.brinco}</strong>{p.animal_nome && <span style={{ color:'var(--text-secondary)', fontSize:13 }}> — {p.animal_nome}</span>}</td>
                <td><span className="badge badge-blue" style={{ textTransform:'capitalize', fontSize:11 }}>{p.categoria || '—'}</span></td>
                <td>{formatDateBR(p.data)}</td>
                <td><strong>{parseFloat(p.peso).toFixed(2)} kg</strong></td>
                <td style={{ color:'var(--text-secondary)', fontSize:13 }}>{p.idade_dias ? `${p.idade_dias}d` : '—'}</td>
                <td>
                  {p.gmd != null ? (
                    <span style={{ color:gmdColor(p.gmd), display:'flex', alignItems:'center', gap:4, fontWeight:600 }}>
                      {gmdArrow(p.gmd)} {formatGd(p.gmd)}
                    </span>
                  ) : <span style={{ color:'var(--text-secondary)', fontSize:13 }}>1ª pesagem</span>}
                </td>
                <td>
                  {p.gmd_acumulado != null ? (
                    <span style={{ color:gmdColor(p.gmd_acumulado), display:'flex', alignItems:'center', gap:4, fontWeight:600 }}>
                      {gmdArrow(p.gmd_acumulado)} {formatGd(p.gmd_acumulado)}
                    </span>
                  ) : <span style={{ color:'var(--text-secondary)', fontSize:13 }}>—</span>}
                </td>
                <td style={{ color:'var(--text-secondary)', fontSize:13, maxWidth:150 }}>{p.observacoes || '—'}</td>
                <td><button className="btn btn-secondary btn-sm" onClick={() => remover(p.id)}>🗑</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legenda dos critérios de classificação do GMD (config-driven, sempre em g/d) */}
      {(() => {
        const { verdeMin, amareloMin } = limiaresGmd(config);
        return (
          <div className="card" style={{ marginTop:16, padding:14 }}>
            <strong style={{ fontSize:13 }}>Critérios de GMD:</strong>
            <div style={{ display:'flex', gap:16, marginTop:6, fontSize:12, flexWrap:'wrap' }}>
              <span><span style={{ color:CORES_GMD.verde.color, fontWeight:'bold' }}>🟢 Verde:</span> ≥ {verdeMin} g/d</span>
              <span><span style={{ color:CORES_GMD.amarelo.color, fontWeight:'bold' }}>🟡 Amarelo:</span> ≥ {amareloMin} e &lt; {verdeMin} g/d</span>
              <span><span style={{ color:CORES_GMD.vermelho.color, fontWeight:'bold' }}>🔴 Vermelho:</span> &lt; {amareloMin} g/d</span>
              <span style={{ color:'var(--text-secondary)' }}>(valores ajustáveis em Configurações — a seta indica só o sinal do GMD, a cor indica o desempenho)</span>
              <span style={{ color:'var(--text-secondary)' }}>GMD Acumulado aparece somente na pesagem mais recente de cada animal.</span>
            </div>
          </div>
        );
      })()}

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && fecharModal()}>
          <div className="modal">
            <div className="modal-header"><h2>Nova Pesagem</h2><button className="btn btn-secondary btn-sm" onClick={fecharModal}>✕</button></div>
            <div className="modal-body">
              <div className="form-group"><label>Animal *</label>
                <AnimalSearchSelect value={form.animal_id} onChange={id => setForm(p => ({ ...p, animal_id:id }))} required />
              </div>
              <div className="form-row">
                <div className="form-group"><label>Data *</label><input type="date" value={form.data} onChange={e => setForm(p => ({ ...p, data:e.target.value }))} /></div>
                <div className="form-group"><label>Peso (kg) *</label><input type="number" step="0.01" value={form.peso} onChange={e => setForm(p => ({ ...p, peso:e.target.value }))} placeholder="Ex: 32.5" /></div>
              </div>
              <div className="form-group"><label>Observações</label><textarea rows={2} value={form.observacoes} onChange={e => setForm(p => ({ ...p, observacoes:e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={fecharModal}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar} disabled={!form.animal_id || !form.peso}>Registrar Pesagem</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
